import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Button,
  Banner,
} from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';

interface DashboardStats {
  totalTryOns: number;
  successful: number;
  successRate: number;
}

interface SyncedProductsResponse {
  products: { tryon_enabled: boolean }[];
  count: number;
}

interface StorefrontStatus {
  themeName: string | null;
  embedEnabled: boolean | null;
  activateUrl: string;
}

interface AnalyticsResponse {
  metrics: {
    totalTryOns: number;
    successful: number;
    successRate: number;
  };
}

/**
 * Shopify Managed Pricing sends the merchant back to the app root with
 * ?charge_id=...&plan_handle=... after they approve a plan. Nothing read those
 * parameters, so the merchant approved a charge and landed on an unchanged
 * dashboard with no word about what happened — the subscription was recorded
 * only in our database, silently.
 */
function approvedPlanFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  if (!params.get('charge_id')) return null;
  const handle = params.get('plan_handle');
  if (!handle) return 'your new plan';
  return handle.charAt(0).toUpperCase() + handle.slice(1);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [approvedPlan] = useState<string | null>(approvedPlanFromUrl);
  const [storefront, setStorefront] = useState<StorefrontStatus | null>(null);
  const [catalogue, setCatalogue] = useState<{ total: number; enabled: number } | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalTryOns: 0,
    successful: 0,
    successRate: 0,
  });

  useEffect(() => {
    // Load real usage stats. A brand-new store correctly shows zeros.
    apiGet<AnalyticsResponse>('/api/analytics?days=30')
      .then((data) => {
        const m = data?.metrics;
        if (m) {
          setStats({
            totalTryOns: m.totalTryOns ?? 0,
            successful: m.successful ?? 0,
            successRate: m.successRate ?? 0,
          });
        }
      })
      .catch((err: unknown) => {
        console.error('[Dashboard] failed to load stats:', err);
      });

    // Whether the widget is switched on in the theme, rather than assuming it
    // is. A theme app embed is off until the merchant turns it on, so on a
    // fresh install the old unconditional "live on storefront" was a false
    // claim the reviewer could disprove in one click.
    apiGet<StorefrontStatus>('/api/storefront-status')
      .then(setStorefront)
      .catch((err: unknown) => {
        console.error('[Dashboard] failed to load storefront status:', err);
      });

    // A catalogue with nothing enabled is the other way the storefront ends up
    // with no try-on button. Shopify's own dev-store sample data is snowboards
    // and gift cards, so a reviewer installing on their own store gets 17
    // products and zero of them recognised as clothing — silence there reads
    // as a broken app.
    apiGet<SyncedProductsResponse>('/api/products/synced')
      .then((data) => {
        const products = data?.products ?? [];
        setCatalogue({
          total: products.length,
          enabled: products.filter((p) => p.tryon_enabled).length,
        });
      })
      .catch((err: unknown) => {
        console.error('[Dashboard] failed to load catalogue summary:', err);
      });
  }, []);

  const nothingEnabled = !!catalogue && catalogue.total > 0 && catalogue.enabled === 0;

  const embedOff = storefront?.embedEnabled === false;
  const embedUnknown = !!storefront && storefront.embedEnabled === null;

  // "Active" only when a shopper can actually see the button: the embed is on and
  // at least one product has try-on enabled. App review (Ref 101523, 5.1.2) saw
  // "Widget live on storefront" next to a catalogue where every product was off.
  const status = embedOff
    ? { value: 'Off', detail: 'App embed not switched on yet' }
    : nothingEnabled
      ? { value: 'No products', detail: 'Switch try-on on for at least one product' }
      : embedUnknown
        ? { value: 'Unknown', detail: 'Could not read your theme; open the theme editor to check the app embed' }
        : { value: 'Active', detail: 'Widget live on storefront' };
  const ready = status.value === 'Active';

  return (
    <Page
      title="Modera Fashion Virtual Try-On"
      primaryAction={{
        content: 'View Analytics',
        onAction: () => navigate('/analytics'),
      }}
      secondaryActions={[
        { content: 'Settings', onAction: () => navigate('/settings') },
        { content: 'Subscription', onAction: () => navigate('/billing') },
      ]}
    >
      <Layout>
        {approvedPlan && (
          <Layout.Section>
            <Banner tone="success" title={`You're now on the ${approvedPlan} plan`}>
              <Text as="p" variant="bodyMd">
                Your subscription is active. Open Subscription to see the try-on
                allowance included with this plan.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {nothingEnabled && (
          <Layout.Section>
            <Banner
              tone="info"
              title="Pick the products customers can try on"
              action={{ content: 'Choose products', onAction: () => navigate('/products') }}
            >
              <Text as="p" variant="bodyMd">
                Try-on is switched off for all {catalogue?.total} synced products, so
                shoppers see no try-on button. Turn it on for the items you want — the
                button appears on those product pages only.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {embedOff && (
          <Layout.Section>
            <Banner
              tone="warning"
              title="Turn on the try-on button in your theme"
              action={{
                content: 'Enable on storefront',
                onAction: () => {
                  const url = storefront?.activateUrl;
                  if (!url) return;
                  // The theme editor cannot open inside the app's iframe.
                  if (window.top) window.top.location.href = url;
                  else window.location.href = url;
                },
              }}
            >
              <Text as="p" variant="bodyMd">
                Shoppers will not see the try-on button until the MODERA app embed is
                switched on{storefront?.themeName ? ` in your ${storefront.themeName} theme` : ''}.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {/* ── Branded hero banner ── */}
        <Layout.Section>
          <div className="modera-hero">
            <p className="modera-hero-title">
              {ready ? 'Virtual Try-On is active' : 'Virtual Try-On is ready to switch on'}
            </p>
            <p className="modera-hero-subtitle">
              Customers can try on your products before buying — powered by Modera AI.
              Check analytics to track performance and conversions.
            </p>
          </div>
        </Layout.Section>

        {/* ── Real usage stat cards ── */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
            <Card>
              <div className="modera-stat-card" style={{ margin: '-16px -16px 0', padding: '0 0 3px' }} />
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Total Try-Ons</Text>
                <span className="modera-stat-value" style={{ fontSize: '32px', fontWeight: 700, lineHeight: 1 }}>
                  {stats.totalTryOns.toLocaleString()}
                </span>
                <Text as="p" variant="bodyMd" tone="subdued">Last 30 days</Text>
              </BlockStack>
            </Card>

            <Card>
              <div className="modera-stat-card" style={{ margin: '-16px -16px 0', padding: '0 0 3px' }} />
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Successful</Text>
                <span className="modera-stat-value" style={{ fontSize: '32px', fontWeight: 700, lineHeight: 1 }}>
                  {stats.successful.toLocaleString()}
                </span>
                <Text as="p" variant="bodyMd" tone="subdued">{stats.successRate}% success rate</Text>
              </BlockStack>
            </Card>

            <Card>
              <div className="modera-stat-card" style={{ margin: '-16px -16px 0', padding: '0 0 3px' }} />
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Status</Text>
                <span className="modera-stat-value" style={{ fontSize: '32px', fontWeight: 700, lineHeight: 1 }}>
                  {status.value}
                </span>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {status.detail}
                </Text>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* ── Quick actions ── */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Quick Actions</Text>
              <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                <Button variant="primary" onClick={() => navigate('/products')}>
                  Manage Products
                </Button>
                <Button onClick={() => navigate('/settings')}>
                  Configure Widget
                </Button>
                <Button onClick={() => navigate('/billing')}>
                  Manage Subscription
                </Button>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ── Getting started ── */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Getting Started</Text>
              <BlockStack gap="100">
                {[
                  'Configure your widget settings to match your store\'s design',
                  'Select which products should have the virtual try-on feature',
                  'Monitor analytics to track customer engagement and conversions',
                ].map((step, i) => (
                  <div key={i} className="modera-step">
                    <span className="modera-step-number">{i + 1}</span>
                    <span className="modera-step-text">{step}</span>
                  </div>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
